import { useRouter } from 'expo-router';
import * as React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Incidental, Reservation } from '@/api';
import { formatIsoInZone, formatNumber, snakeToTitleCase } from '@/core';
import { GREY_40 } from '@/style/colors';
import { Button, Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { ChevronRight } from '@/ui/icons/chevron-right';

export interface IncidentalsProps {
  reservation: Reservation;
}

export default function Incidentals({ reservation }: IncidentalsProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();

  // Render incidental timestamps in the reservation's pickup location zone
  // so they don't shift to the viewer's local time.
  const pickupTz = reservation.pickupLocation?.timezone || 'UTC';

  const formatDate = (d: string) =>
    d ? formatIsoInZone(d, pickupTz, 'MM.dd.yyyy') : undefined;

  const formatTime = (d: string) =>
    d ? formatIsoInZone(d, pickupTz, 'hh:mm a') : undefined;

  const handleViewAttachments = (incidental: Incidental) => {
    router.push(
      `/account/trip-history/${reservation.id}/incidentals/${incidental.id}`
    );
  };

  return (
    <View style={styles.container}>
      {reservation?.incidentals.map((incidental: Incidental) => (
        <Card key={incidental.id} style={{ gap: 8, padding: 16 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <View>
              <Text
                tx="incidentals.name"
                type="caption1"
                style={{ color: GREY_40 }}
              />

              <Text type="bodyRegular">
                {snakeToTitleCase(incidental.category)}
              </Text>
            </View>

            <View>
              <Button
                tx="incidentals.attachments"
                type="secondaryExtraSmall"
                onPress={() => handleViewAttachments(incidental)}
                rightIcon={
                  <Icon
                    type="naked"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />
                }
                style={{ width: 120 }}
              />
            </View>
          </View>

          <View>
            <Text
              tx="incidentals.date"
              type="caption1"
              style={{ color: GREY_40 }}
            />

            <Text type="bodyRegular">{`${formatDate(
              incidental.offenseDate
            )} | ${formatTime(incidental?.offenseDate)}`}</Text>
          </View>

          <View>
            <Text
              tx="incidentals.cost"
              type="caption1"
              style={{ color: GREY_40 }}
            />

            <Text type="bodyRegular">{`$${formatNumber(
              incidental.amountWithUpcharge
            )}`}</Text>
          </View>

          <View>
            <Text
              tx="incidentals.processingFee"
              type="caption1"
              style={{ color: GREY_40 }}
            />

            <Text type="bodyRegular">{`$${incidental.adminFee / 100}`}</Text>
          </View>

          {incidental.isPaid && (
            <View>
              <Text
                tx="incidentals.paymentStatus"
                type="caption1"
                style={{ color: GREY_40 }}
              />

              <Text type="bodyRegular">
                {incidental.isPaidAt
                  ? `Paid  ${formatIsoInZone(
                      incidental.isPaidAt,
                      pickupTz,
                      'MMMM d, yyyy'
                    )}`
                  : 'Paid'}
              </Text>
            </View>
          )}
        </Card>
      ))}

      {reservation?.incidentals.length === 0 ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            You don't have any incidentals
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    backgroundColor: theme.colors.background_1,
    flexGrow: 1,
    flexDirection: 'column',
    borderRadius: 24,
    gap: 16,
    paddingBottom: 48,
  },
}));
